import { ProfileData } from "~/types/profile.types";

/**
 * `select` functions for the `profile` query. Keep these at module scope so
 * their identity is stable across renders — that is what lets TanStack Query
 * skip re-running them, and (paired with its structural sharing of the result)
 * re-render a component only when the slice it reads actually changes.
 *
 * Inline selectors (`useProfile((p) => ...)` or `useProfileData(p => ...) in a component body) get a new
 * identity every render and defeat this — always reference one from here.
 *
 * Selectors receive `NormalizedProfile | null | undefined` because the query is
 * `null` while logged out and `undefined` before it resolves. Prefer returning primitives
 * (cheapest equality); object slices are fine too — structural sharing dedupes
 * deeply-equal results.
 */

/** The user's SSO id — e.g. for linking to their public profile. */
const selectSSOid = (profile: ProfileData): string | undefined =>
  profile?.SSOid;

/** The user's Google Analytics client id — e.g. for attributing a quote request. */
const selectGaClientId = (profile: ProfileData): string | undefined =>
  profile?.gaClientId;

/** Whether a profile carrying an `SSOid` is present (the logged-in signal). */
const selectIsLoggedIn = (profile: ProfileData): boolean =>
  Boolean(profile?.SSOid);

/** The user's email. */
const selectEmail = (profile: ProfileData): string | undefined =>
  profile?.email;
const selectEmailVerified = (profile: ProfileData): boolean =>
  Boolean(profile?.emailVerified);

const selectAvatar = (profile: ProfileData): string | undefined =>
  profile?.avatarUrl;

const selectEmailUnsubscribes = (profile: ProfileData) =>
  profile?.emailUnsubscribes?.all;

const selectName = (profile: ProfileData) => ({
  firstName: profile?.firstName,
  lastName: profile?.lastName,
});

const selectPhoneFormatted = (profile: ProfileData) => profile?.phoneFormatted;
const selectPhoneVerified = (profile: ProfileData) =>
  Boolean(profile?.phoneVerified);
const selectPhoneE164 = (profile: ProfileData) => profile?.phoneE164;

const selectOrgProfession = (profile: ProfileData) => ({
  organization: profile?.organization,
  profession: profile?.profession,
});

const selectExtUserId = (profile: ProfileData) => profile?.extUserId;

/** Whether the user hosts spaces. */
const selectIsHost = (profile: ProfileData): boolean =>
  Boolean(profile?.isHost);

export {
  selectAvatar,
  selectEmail,
  selectEmailVerified,
  selectEmailUnsubscribes,
  selectExtUserId,
  selectGaClientId,
  selectIsHost,
  selectIsLoggedIn,
  selectName,
  selectOrgProfession,
  selectPhoneE164,
  selectPhoneFormatted,
  selectPhoneVerified,
  selectSSOid,
};
